import { API } from '../../lib/API';
import Logger from '../../lib/Logger';
import { LANGUAGE, LOCAL_STORAGE, URL_SEARCH_PARAM, BRAZE_EVENTS, } from '../utils.constant';
import { AnalyticsType } from '../../types/Analytics';
import { getReferralChannel, sanitizeParams, } from '../auth/check-params';
import { API_ENDPOINTS } from '../../lib/API/api-endpoints.constant';
import { AnalyticsUtils } from './AnalyticsUtils';
import { Braze } from './Braze';
import { analyticsEmitter } from './AnalyticsEventEmitter';
class Amplitude {
    constructor() { }
    static parseData({ eventName, eventProperties = {}, userProperties = {}, extraProperties = {}, }) {
        const deviceProperties = AnalyticsUtils.getDeviceInfo() || {};
        const session_id = this.amplitudeInstance?.getSessionId() || -1;
        this.updateSessionId(session_id);
        const data = {
            device_id: AnalyticsUtils.getDeviceId(),
            event_name: eventName,
            user_properties: {
                ...userProperties,
                ...(!!AnalyticsUtils.getPage() && {
                    'Landing Page': AnalyticsUtils.getPage(),
                }),
            },
            event_properties: {
                ...eventProperties,
                Pagetype: AnalyticsUtils.getPage(),
                Url: sanitizeParams(),
                'export video count': userProperties.export_video_count || 0,
                'export project count': userProperties.export_project_count || 0,
                daysSinceSignup: userProperties.daysSinceSignup,
                weeksSinceSignup: userProperties.weeksSinceSignup,
            },
            extra_properties: {
                ...deviceProperties,
                ...extraProperties,
                State: userProperties?.id ? 'Logged In' : 'Logged Out',
                'Device Type': !!Amplitude.deviceDetails?.os?.name
                    ? Amplitude.deviceDetails.os.name
                    : '',
                session_id: session_id,
            },
            timestamp: new Date().getTime(),
        };
        if (userProperties?.id) {
            data['user_id'] = userProperties.id;
        }
        else {
            data['user_id'] = null;
        }
        if (eventName === 'Click Signup') {
            localStorage.setItem(LOCAL_STORAGE.BUTTON_POSITION, window.btoa(eventProperties.btnPosition));
            data.event_properties['Button Position'] = eventProperties.btnPosition;
            delete data.event_properties.btnPosition;
        }
        return data;
    }
    static setNewIdentity(identity, referralParams) {
        Object.keys(referralParams).forEach(key => {
            if (key.match(this.CUSTOM_AMPLITUDE_PROPERTIES_REGEX) &&
                !!referralParams[key]) {
                if ([URL_SEARCH_PARAM.MS_CLK_ID, URL_SEARCH_PARAM.FB_CL_ID].includes(key)) {
                    identity.setOnce(`${key}`, referralParams[key]);
                }
                else {
                    identity.setOnce(`initial_${key}`, referralParams[key]);
                }
            }
        });
    }
    static logEvent(instance, lang) {
        Logger.logEvent(AnalyticsType.AMPLITUDE, 'Initialization Complete');
    }
    static async init(lang, config, callback) {
        const amplitude = await import('amplitude-js');
        const referralParams = getReferralChannel();
        amplitude.getInstance().setUserId(null);
        amplitude.getInstance().init(config.AMPLITUDE_ID, AnalyticsUtils.getUserId()?.toString() || '', {
            includeGclid: true,
            includeReferrer: true,
            includeUtm: true,
        }, async (instance) => {
            const userProperties = {};
            AnalyticsUtils.setDeviceId(instance.options.deviceId);
            Amplitude.deviceDetails = instance['_ua'];
            if (!!AnalyticsUtils.getPage() && !!referralParams) {
                userProperties['Landing Page'] =
                    AnalyticsUtils.getPage();
            }
            const identity = new amplitude.Identify();
            identity.setOnce(`Initial Url`, sanitizeParams());
            if (referralParams) {
                Amplitude.setNewIdentity(identity, referralParams);
                if (referralParams.Channel) {
                    userProperties.Channel =
                        referralParams.Channel;
                    AnalyticsUtils.setChannel(referralParams.Channel);
                }
            }
            instance.identify(identity);
            userProperties['Session Start Time'] = new Date().toISOString();
            userProperties['screen_width'] = window.screen.width;
            userProperties['screen_height'] = window.screen.height;
            instance.setUserProperties(userProperties);
            Amplitude.logEvent(instance, lang);
            Amplitude.amplitudeInstance = instance;
            Amplitude.isReady = true;
            analyticsEmitter.emit('amplitude', { isReady: true });
            callback();
        });
    }
    static load({ language = 'en', config = {}, callback = () => { }, }) {
        if (!window) {
            return;
        }
        let lang = 'English';
        if (language in LANGUAGE) {
            lang = LANGUAGE[language];
        }
        Amplitude.init(lang, config, callback);
    }
    static send(eventName, eventProperties = {}, userProperties = {}, extraProperties = {}) {
        if (!window) {
            return;
        }
        if (eventName === 'App Load' && Amplitude.amplitudeInstance) {
            const sanitizedUser = AnalyticsUtils.getSanitizedUser();
            Amplitude.amplitudeInstance.logEvent(eventName, {
                ...eventProperties,
                Url: sanitizeParams(),
                ...sanitizedUser,
                ...userProperties,
            }, () => {
                Logger.logEvent(AnalyticsType.AMPLITUDE, eventName);
            });
            return;
        }
        const sanitizedUser = AnalyticsUtils.getSanitizedUser();
        const data = this.parseData({
            eventName,
            eventProperties: {
                ...eventProperties,
            },
            userProperties: {
                ...sanitizedUser,
                ...userProperties,
            },
            extraProperties,
        });
        if (BRAZE_EVENTS.hasOwnProperty(eventName) && AnalyticsUtils.userData) {
            const brazeEvent = {
                eventName: eventName,
                eventProperties: data?.event_properties,
                userProperties: data?.user_properties,
            };
            if (Braze.isReady) {
                Braze.sendEvent(brazeEvent);
            }
            else {
                analyticsEmitter.on('braze', ({ isReady }) => {
                    if (isReady) {
                        Braze.sendEvent(brazeEvent);
                    }
                });
            }
        }
        this.http
            .post(API_ENDPOINTS.AMPLITUDE_EVENT, data)
            .then(() => {
            Logger.logEvent(AnalyticsType.AMPLITUDE, `${eventName}`);
        })
            .catch(error => {
            console.error(error);
        });
    }
    static updateSessionId(newSessionId) {
        const oldSessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
        if (newSessionId && oldSessionId !== newSessionId) {
            localStorage.setItem(LOCAL_STORAGE.SESSION_ID, newSessionId);
        }
    }
}
Amplitude.http = API;
Amplitude.amplitudeInstance = null;
Amplitude.CUSTOM_AMPLITUDE_PROPERTIES_REGEX = /^((?!utm_(source|medium|campaign|term|content)|referrer|referring_domain|gclid|Channel).)*$/;
Amplitude.isReady = false;
export { Amplitude };
